import { QuestionCircleOutlined, UnlockOutlined } from "@ant-design/icons";
import {
	Button,
	Divider,
	Flex,
	Input,
	Popconfirm,
	Popover,
	Space,
	Tooltip,
} from "antd";
import dayjs from "dayjs";
import React, { useContext, useEffect, useMemo, useState } from "react";
import TimewritingStatusBadge from "../components/Badge/TimewritingStatusBadge";
import CalendarCell from "../components/CalendarCell";
import TimesheetReviewHistoryPopover from "../components/Popover/TimesheetReviewHistoryPopover";
import SearchButton from "../components/SearchButton";
import UserImage from "../components/UserImage";
import WeekNavigator from "../components/WeekNavigator";
import { TimeEntryProvider } from "../contexts/TimeEntryContext";
import { useTimewriting } from "../contexts/TimewritingContext";
import { updateSubmittedReview } from "../services/api-server/submitted_review";
import { updateUserWeeklyApproval } from "../services/api-server/timewriting";
import { GetAntIcon } from "../utils/ant_icons";
import Emitter from "../utils/emitter";
import {
	checkSuperAdminPriviledge,
	convertToHoursAndMinutesString,
} from "../utils/utils";
import { HomeContext } from "./Home";
interface TimewritingCalendarProps {
	project?: any;
	data: any[];
	headerComponent?: React.ReactNode;
	type: "project" | "user";
	cellClassName?: (cell: any) => string;
}

function Label({
	type,
	item,
	project,
	handleResubmit,
}: {
	type: "project" | "user";
	item: any;
	handleResubmit: () => void;
	project?: any;
}) {
	const { userWeekSubmittedReview, allUserWeekSubmittedReview } =
		useTimewriting();

	const { userInfo }: any = useContext(HomeContext);

	const getBadgeStatus = () => {
		const status = userWeekSubmittedReview?.submissions?.find(
			(submission: any) => submission.project_id == item._id
		)?.status;
		return (
			<>
				<Flex gap={12} align="center">
					<TimesheetReviewHistoryPopover
						project_id={item._id}
						user={userInfo.user.email}
					>
						<TimewritingStatusBadge status={status} />
					</TimesheetReviewHistoryPopover>
					{status === "rejected" && (
						<Button onClick={() => handleResubmit()}>Resubmit</Button>
					)}
				</Flex>
			</>
		);
	};

	if (type === "project") {
		return (
			<Flex gap={"8px"} style={{ width: "100%" }} vertical>
				<div>{item.project_name}</div>
				<Flex
					justify="start"
					gap={8}
					style={{ fontSize: "12px", color: "grey" }}
				>
					<Tooltip title={item.tenant_name}>
						<div
							style={{
								width: "108px",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{GetAntIcon("apartment")} {item.tenant_name}
						</div>
					</Tooltip>

					<Tooltip title={item.client_name}>
						<div
							style={{
								width: "108px",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{GetAntIcon("profile")} {item.client_name}
						</div>
					</Tooltip>
				</Flex>

				{userWeekSubmittedReview && getBadgeStatus()}
			</Flex>
		);
	} else {
		const userTotalSubmittedHours = item?.time_entries?.reduce(
			(acc: number, item: any) =>
				acc + item.duration_hours * 60 + item.duration_mins,
			0
		);
		const userTotalSubmittedHoursString = convertToHoursAndMinutesString(
			userTotalSubmittedHours
		);

		const userSubmittedReview = allUserWeekSubmittedReview.find(
			(submitted_review: any) => submitted_review.user === item.user
		);
		const projectSubmission = userSubmittedReview?.submissions.find(
			(submission: any) => submission.project_id === project._id
		);
		const submissionDate =
			projectSubmission?.history[projectSubmission?.history.length - 1]
				?.submitted_date;
		return (
			<Flex gap={"12px"} align="center">
				<UserImage email={item.user} size={32} />
				<Space direction="vertical">
					<span>{item.user}</span>
					<Popover
						rootClassName="review-history-popover"
						content={
							<Flex gap={"20px"}>
								<span>Total submitted hours</span>
								<span style={{ fontWeight: "bold" }}>
									{userTotalSubmittedHoursString}
								</span>
							</Flex>
						}
						placement="right"
						trigger={["hover"]}
					>
						<span style={{ whiteSpace: "nowrap" }}>
							{dayjs(submissionDate).format("DD MMM YYYY HH:mm:ss")}
						</span>
					</Popover>
				</Space>
			</Flex>
		);
	}
}

function ApproveButton({ handleApprove }: { handleApprove: () => void }) {
	return (
		<Popconfirm
			icon={<QuestionCircleOutlined style={{ color: "grey" }} />}
			title={
				<div style={{ maxWidth: "330px" }}>
					Approve this timesheet? Once approved it will be finalised and cannot
					be modified.
				</div>
			}
			rootClassName="review-popconfirm"
			placement="bottomLeft"
			cancelButtonProps={{ ghost: true }}
			okText="Confirm"
			onConfirm={() => handleApprove()}
		>
			<Button className="approve-btn" type="link">
				Approve
			</Button>
		</Popconfirm>
	);
}

function RejectButton({
	handleReject,
}: {
	handleReject: (comment: string) => void;
}) {
	const [comment, setComment] = useState<string>("");
	return (
		<Popconfirm
			icon={<QuestionCircleOutlined style={{ color: "grey" }} />}
			title={
				<div style={{ maxWidth: "330px" }}>
					<span>Reject this timesheet? Please provide a comment.</span>
					<br />
					<br />
					<b>Note</b>: This timesheet can be resubmitted for review.
					<Input.TextArea
						value={comment}
						onChange={(e) => setComment(e.target.value)}
						autoSize={{ minRows: 2, maxRows: 6 }}
					/>
				</div>
			}
			rootClassName="review-popconfirm"
			placement="bottomLeft"
			cancelButtonProps={{ ghost: true }}
			okText="Confirm"
			okButtonProps={{ ghost: true, disabled: comment?.length === 0 }}
			onOpenChange={(visible) => {
				if (!visible) setComment("");
			}}
			onConfirm={() => {
				handleReject(comment);
				setComment("");
			}}
		>
			<Button className="reject-btn" type="link">
				Reject
			</Button>
		</Popconfirm>
	);
}

function UnlockButton({ handleUnlock }: { handleUnlock: () => void }) {
	return (
		<Popconfirm
			icon={<QuestionCircleOutlined style={{ color: "grey" }} />}
			title={
				<div className="max-w-[330px] flex flex-col gap-4">
					<p>Unlock timesheet? Unlocking will let you review it again.</p>
					<p>
						To return it to the owner, you will need to reject after unlocking.
					</p>
				</div>
			}
			rootClassName="review-popconfirm"
			placement="bottomLeft"
			cancelButtonProps={{ ghost: true }}
			okText="Confirm"
			onConfirm={() => {
				handleUnlock();
			}}
		>
			<Button icon={<UnlockOutlined />} type="link">
				Unlock
			</Button>
		</Popconfirm>
	);
}

function ReviewSection({
	handleApproval,
	item,
	project,
	isSuperOrTenantAdmin,
}: {
	handleApproval: (
		item: any,
		status: string,
		comment?: string,
		unlock?: boolean
	) => void;
	item: any;
	project: any;
	isSuperOrTenantAdmin: boolean;
}) {
	const { allUserWeekSubmittedReview } = useTimewriting();
	const [showStatus, setShowStatus] = useState<boolean>(false);
	const [status, setStatus] = useState<string>("");

	useEffect(() => {
		const userSubmittedReview = allUserWeekSubmittedReview.find(
			(submitted_review: any) => submitted_review.user === item.user
		);
		const projectSubmission = userSubmittedReview?.submissions.find(
			(submission: any) => submission.project_id === project._id
		);
		const status = projectSubmission?.status;
		setStatus(status);
		if (status === "approved" || status === "rejected") setShowStatus(true);
		else setShowStatus(false);
	}, [allUserWeekSubmittedReview, item, project]);

	return (
		<Flex className="timewriting-grid-cell" justify="center" align="center">
			{showStatus ? (
				<>
					<TimesheetReviewHistoryPopover
						project_id={project._id}
						user={item.user}
					>
						<TimewritingStatusBadge status={status} className="mx-[15px]!" />
					</TimesheetReviewHistoryPopover>
					<Divider type="vertical" />
					{status === "approved" && isSuperOrTenantAdmin && (
						<UnlockButton
							handleUnlock={() =>
								handleApproval(item, "submitted", undefined, true)
							}
						/>
					)}
				</>
			) : (
				<>
					<ApproveButton
						handleApprove={() => handleApproval(item, "approved")}
					/>
					<Divider type="vertical" />
					<RejectButton
						handleReject={(comment: string) =>
							handleApproval(item, "rejected", comment)
						}
					/>
				</>
			)}
		</Flex>
	);
}

const TimewritingCalendar = ({
	project,
	data,
	headerComponent,
	type,
	cellClassName,
}: TimewritingCalendarProps) => {
	const { userInfo, currentTenantData }: any = useContext(HomeContext);
	const {
		currentWeekStart,
		currentWeekEnd,
		daysOfWeek,
		fetchTimewritingUpdate,
	} = useTimewriting();

	const isSuperOrTenantAdmin = useMemo(() => {
		return (
			checkSuperAdminPriviledge(userInfo?.userRole) ||
			currentTenantData?.tenant_admin
				?.map((adminEmail: string) => adminEmail.toLowerCase())
				.includes(userInfo?.user.email.toLowerCase())
		);
	}, [userInfo, currentTenantData]);

	const [filteredData, setFilteredData] = useState<any[] | null>(null);

	const calculateDurationInDay = (day: string) => {
		const timeEntries = data.flatMap((item: any) => item.time_entries);
		const timeEntriesInDay = timeEntries.filter((item: any) =>
			dayjs(item.date).isSame(dayjs(day), "day")
		);
		const totalDurationMin = timeEntriesInDay.reduce(
			(acc: number, item: any) =>
				acc + item.duration_hours * 60 + item.duration_mins,
			0
		);
		return totalDurationMin;
	};

	const getNoDataMessage = (type: "project" | "user") => {
		if (type === "user") return "No submitted timesheet for this week.";
		if (type === "project") return "You are not assigned to any projects.";
		return "No members available for this project.";
	};

	const handleApproval = async (
		item: any,
		statusChanged: string,
		comment?: string,
		unlock?: boolean
	) => {
		const projectId = project ? project._id : item._id;
		const email = item.user || userInfo?.user.email;

		try {
			await updateUserWeeklyApproval(
				projectId,
				email,
				{
					firstDayOfWeek: currentWeekStart,
					lastDayOfWeek: currentWeekEnd,
					reviewer:
						statusChanged !== "resubmitted" ? userInfo?.user.email : null,
				},
				statusChanged,
				comment
			);

			await updateSubmittedReview(
				email,
				currentWeekStart,
				currentWeekEnd,
				[projectId],
				statusChanged,
				comment,
				userInfo?.user
			);

			Emitter.emit("alert", {
				type: "success",
				message: `Timesheet has been successfully ${unlock ? "unlocked" : statusChanged}.`,
				description: "",
				top: true,
				closeable: false,
				timeout: 3000,
			});

			fetchTimewritingUpdate();
		} catch (error) {
			Emitter.emit("alert", {
				type: "alert",
				message: `Fail to update timesheet.`,
				description: "",
				top: true,
				closeable: false,
				timeout: 3000,
			});
		}
	};

	const handleSearch = (e: any) => {
		if (e.value === "") {
			setFilteredData(null);
			return;
		} else {
			const filteredData = data.filter((item: any) =>
				item[e.columnKey]?.toLowerCase().includes(e.value.toLowerCase())
			);
			setFilteredData(filteredData);
		}
	};

	return (
		<>
			<div className="week-navigator">
				{headerComponent ? (
					React.cloneElement(headerComponent as React.ReactElement, {
						children: <WeekNavigator />,
					})
				) : (
					<WeekNavigator />
				)}
			</div>
			<div style={{ overflow: "auto" }}>
				<div
					className="timewriting-grid-table"
					style={{
						gridTemplateColumns:
							type == "project"
								? "auto repeat(7, 160px)"
								: "auto repeat(7, 160px) auto",
					}}
				>
					<div className="timewriting-grid-header timewriting-grid-cell timewriting-row-header">
						<SearchButton
							handleSearch={handleSearch}
							headerName={
								type == "project" ? "List of projects" : "List of team members"
							}
							columnKey={type == "project" ? "project_name" : "user"}
							searchBarType="panel"
						/>
					</div>
					{Array.from({ length: 7 }, (_, i) => (
						<div
							key={i}
							className="timewriting-grid-header timewriting-grid-cell timewriting-row-header"
						>
							<Flex
								key={i}
								align="center"
								gap={24}
								justify="space-between"
								style={{ width: "100%" }}
							>
								<Space direction="vertical">
									<span style={{ fontSize: "20px" }}>
										{dayjs(currentWeekStart).add(i, "day").format("DD")}
									</span>
									<span>
										{dayjs(currentWeekStart).add(i, "day").format("dddd")}
									</span>
								</Space>
								{/* Total hours and minutes for the day */}
								<div className="color-default-btn">
									{convertToHoursAndMinutesString(
										calculateDurationInDay(daysOfWeek[i]?.day)
									)}
								</div>
							</Flex>
						</div>
					))}
					{type == "user" && (
						<div className="timewriting-grid-header timewriting-grid-cell timewriting-row-header">
							Review
						</div>
					)}
					{/* Table content */}
					{(filteredData ?? data).length > 0 ? (
						(filteredData ?? data).map((item: any) => {
							return (
								<>
									<div className="timewriting-grid-header timewriting-grid-cell">
										<Label
											type={type}
											item={item}
											project={project}
											handleResubmit={() => handleApproval(item, "resubmitted")}
										/>
									</div>{" "}
									{Array.from({ length: 7 }, (_, i) => {
										const timeEntries = item.time_entries.filter(
											(timeEntry: any) => timeEntry.date === daysOfWeek[i]?.day
										);
										return (
											<TimeEntryProvider>
												<CalendarCell
													key={i}
													day={daysOfWeek[i]?.day}
													timeEntries={timeEntries}
													item={item}
													className={`timewriting-grid-cell ${
														cellClassName && cellClassName(item)
													}`}
													closeFutureDate={type === "project"}
													cellClickable={type === "project"}
												/>
											</TimeEntryProvider>
										);
									})}
									{type == "user" && (
										<ReviewSection
											handleApproval={handleApproval}
											item={item}
											project={project}
											isSuperOrTenantAdmin={isSuperOrTenantAdmin}
										/>
									)}
								</>
							);
						})
					) : (
						<div
							className="timewriting-grid-cell"
							style={{
								gridColumn: "1 / -1",
								display: "flex",
								justifyContent: "center",
								height: "150px",
							}}
						>
							{getNoDataMessage(type)}
						</div>
					)}
				</div>
			</div>
		</>
	);
};

export default TimewritingCalendar;
